import {useEffect, useMemo} from 'react';

import {useNavigate} from 'react-router-dom';
import {ForWhom, RegistrationCandidateDetails} from 'store/candidate/types';
import {ROUTES, mapServerError} from 'core';
import {setCandidate} from 'store/candidate/candidateSlice';
import {useAppDispatch} from 'store/hooks';
import {
    useChildDetailsValidateMutation,
    useParentDetailsValidateMutation,
    useValidateCandidateMutation,
} from 'store/candidate/candidateAPI';

import {ServerError} from '@britishcouncil/react-forms';

export const useCandidateDetailsSubmit = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [validateCandidate, validateCandidateData] = useValidateCandidateMutation();
    const [validateParent, validateParentData] = useParentDetailsValidateMutation();
    const [validateChild, validateChildData] = useChildDetailsValidateMutation();

    const baseCandidateValidation = (formModel: RegistrationCandidateDetails) => {
        validateCandidate(formModel)
            .unwrap()
            .then(() =>
                validateParent(formModel)
                    .unwrap()
                    .then(() => onSuccess(formModel))
                    .catch(_ => {}))
            .catch(_ => {})
    };

    const submit = (formModel: RegistrationCandidateDetails) => {
        if (formModel.forWhom !== ForWhom.Myself) {
            validateChild(formModel)
                .unwrap()
                .then(() => baseCandidateValidation(formModel))
                .catch(rejected => console.error(rejected));
        } else {
            baseCandidateValidation(formModel);
        }
    };

    const onSuccess = (formModel: RegistrationCandidateDetails) => {
        dispatch(setCandidate(formModel));
        navigate(ROUTES.CANDIDATE_PRODUCT_DETAILS);
    };

    const serverErrors = useMemo<ServerError[]>(() => {
        let lastMutation = [validateChildData, validateCandidateData, validateParentData].sort(
            (x, y) => (y.startedTimeStamp ?? 0) - (x.startedTimeStamp ?? 0)
        )[0];

        if (!lastMutation.isError) return [];

        return mapServerError(lastMutation.error);
    }, [validateCandidateData, validateParentData, validateChildData]);

    return {
        submit,
        serverErrors,
        isSubmitting: validateCandidateData.isLoading || validateParentData.isLoading,
    };
};
